import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useGetTeacher } from '../../api/generated/enrollment/enrollment';
import {
  getGetTeacherRateQueryKey,
  useGetTeacherRate,
  usePutTeacherRate,
} from '../../api/generated/payroll/payroll';
import type { Me, TeacherRateVigency } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { formatMoney } from '../billing/money';
import { type ReauthGate, isReauthRequired, useReauthGate } from '../reauth/ReauthGate';
import { AdminOnly } from './AdminOnly';
import styles from './enrollment.module.css';

/**
 * Ficha de um professor.
 *
 * O CADASTRO continua somente leitura: o contrato nao tem operacao de alteracao
 * de professor, e a tela nao inventa uma. O VALOR POR AULA, sim, e definido
 * aqui — ele nunca pertenceu ao cadastro (tem vigencia e historico proprios, em
 * `putTeacherRate`), e ate esta secao existir nao havia caminho nenhum pela
 * interface para defini-lo.
 *
 * Esta e a rota certa para ele por um motivo simples: e a unica tela que ja fala
 * de UM professor. Uma pagina propria de valores exigiria escolher o professor
 * de novo, num seletor que ja existe aqui em forma de link.
 */
export function TeacherDetailPage() {
  const params = useParams();
  const teacherId = params['teacherId'] ?? '';

  return <AdminOnly>{(me) => <TeacherDetailView teacherId={teacherId} me={me} />}</AdminOnly>;
}

function TeacherDetailView({ teacherId, me }: { readonly teacherId: string; readonly me: Me }) {
  const { t } = useTranslation();
  const query = useGetTeacher(teacherId);

  if (query.isError) return <Alert tone="error">{t('error.loadFailed')}</Alert>;
  const teacher = query.data;
  if (!teacher) return null;

  return (
    <div className={styles.page}>
      <p>
        <Link to="/teachers">{t('enrollment.teachers.backToList')}</Link>
      </p>
      <h1>{teacher.displayName}</h1>

      <dl className={styles.definitions}>
        <dt>{t('enrollment.email')}</dt>
        <dd>{teacher.email}</dd>
        <dt>{t('enrollment.timezone')}</dt>
        <dd>{teacher.timezone}</dd>
        <dt>{t('enrollment.locale')}</dt>
        <dd>{t(`language.${teacher.locale}`)}</dd>
        <dt>{t('enrollment.activeSince')}</dt>
        <dd>{teacher.activeSince ?? '—'}</dd>
        <dt>{t('enrollment.status')}</dt>
        <dd>{teacher.active ? t('enrollment.active') : t('enrollment.inactive')}</dd>
      </dl>

      {/*
        A secao so monta com o professor em maos: e ela que dispara a leitura do
        valor por aula, e pedir o valor de um professor que nao existe seria uma
        chamada garantidamente 404.
      */}
      <TeacherRateSection teacherId={teacher.personId} locale={me.locale} />
    </div>
  );
}

/**
 * O valor por aula do professor: o vigente, o historico e o formulario.
 *
 * **Esta secao e o que tira a folha do zero.** Professor cadastrado pela SPA
 * nasce com `per_class_amount = 0` (enrollment/teacher.go); a derivacao congela
 * esse zero no item da aula, o fechamento da competencia aborta a transacao
 * inteira e o lote responde 409 — nao ha lote, nao ha demonstrativo e nao ha
 * pagamento. A cadeia era fechada: `putTeacherRate` existia no contrato, no
 * cliente gerado e no servidor, e nenhuma tela chamava.
 *
 * O formulario fica atras do portao de reautenticacao porque a rota e
 * `Sensitive: true` em app/policies.go — quem salvar sem credencial reconfirmada
 * leva 403. O portao nasce AQUI, e nao dentro do formulario, para que o dialogo
 * e o aviso da janela aparecam uma vez so na secao.
 */
function TeacherRateSection({
  teacherId,
  locale,
}: {
  readonly teacherId: string;
  readonly locale: string;
}) {
  const { t } = useTranslation();
  const gate = useReauthGate();
  const rateQuery = useGetTeacherRate(teacherId);
  const rate = rateQuery.data;

  return (
    <section aria-labelledby="teacher-rate">
      <h2 id="teacher-rate">{t('enrollment.teachers.rate.title')}</h2>
      <p className={styles.maskHelp}>{t('enrollment.teachers.rate.explained')}</p>

      {gate.dialog}

      {rateQuery.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}

      {rate ? (
        <>
          {/*
            O aviso nomeia a CONSEQUENCIA, e nao so a ausencia: "sem valor por
            aula" nao diz a ninguem que a folha do mes inteiro nao fecha.
          */}
          {rate.current === null ? (
            <Alert tone="warning" title={t('enrollment.teachers.rate.noneTitle')}>
              {t('enrollment.teachers.rate.noneDetail')}
            </Alert>
          ) : (
            <p>
              <strong>{t('enrollment.teachers.rate.current')}</strong>{' '}
              {formatMoney(rate.current.amount, locale)} ·{' '}
              {t('enrollment.teachers.rate.since', { date: rate.current.validFrom })}
              {rate.current.provisional ? ` · ${t('enrollment.teachers.rate.provisionalTag')}` : ''}
            </p>
          )}

          <RateHistory history={rate.history} locale={locale} />
          <RateForm teacherId={teacherId} locale={locale} gate={gate} />
        </>
      ) : null}
    </section>
  );
}

/**
 * O historico inteiro, e nao apenas o valor de hoje.
 *
 * Alterar o valor por aula nao reescreve o passado: a aula ja apurada guarda o
 * valor congelado nela. Sem o historico na tela, quem confere um pagamento
 * antigo nao tem como saber por qual vigencia ele foi calculado.
 */
function RateHistory({
  history,
  locale,
}: {
  readonly history: readonly TeacherRateVigency[];
  readonly locale: string;
}) {
  const { t } = useTranslation();

  return (
    <>
      <h3>{t('enrollment.teachers.rate.history')}</h3>
      {history.length === 0 ? (
        <p className={styles.maskHelp}>{t('enrollment.teachers.rate.historyEmpty')}</p>
      ) : (
        <ul>
          {history.map((vigency) => (
            <li key={`${vigency.validFrom}-${vigency.amount.amountCents}`}>
              {formatMoney(vigency.amount, locale)} ·{' '}
              {t('enrollment.teachers.rate.since', { date: vigency.validFrom })}
              {vigency.provisional ? ` · ${t('enrollment.teachers.rate.provisionalTag')}` : ''}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function RateForm({
  teacherId,
  locale,
  gate,
}: {
  readonly teacherId: string;
  readonly locale: string;
  readonly gate: ReauthGate;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const save = usePutTeacherRate();

  // Centavos como TEXTO, pelo mesmo motivo da antecedencia do lembrete e do
  // preco: apagar o campo precisa significar vazio, e nao zero. Aqui o zero e
  // ainda pior — e o unico valor que o servidor recusa com 422 (`NewClassRate`),
  // porque uma vigencia de zero entraria ratificada e congelaria zero na proxima
  // aula, num item que nenhuma consulta reescreve depois.
  const [amountCents, setAmountCents] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [saved, setSaved] = useState(false);

  const digitsOnly = /^\d+$/.test(amountCents.trim());
  const parsed = digitsOnly ? Number.parseInt(amountCents.trim(), 10) : Number.NaN;
  const validAmount = Number.isInteger(parsed) && parsed > 0;
  const canSave = validAmount && validFrom !== '';
  // O 403 do portao ja e respondido pelo dialogo; desenhar tambem o alerta
  // generico seria dizer duas coisas sobre a mesma recusa.
  const unexpected = save.error !== null && !isReauthRequired(save.error);

  return (
    <form
      className={styles.form}
      aria-label={t('enrollment.teachers.rate.form')}
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSave) return;
        setSaved(false);
        gate.run((onReauthRequired) =>
          save.mutate(
            {
              teacherId,
              data: {
                amount: { amountCents: parsed, currency: 'BRL' },
                validFrom,
              },
            },
            {
              onSuccess: () => {
                setSaved(true);
                setAmountCents('');
                // `invalidateQueries` da chave, e nao `refetch()` desta leitura:
                // a vigencia nova muda o valor vigente E o historico, que sao a
                // mesma consulta, e qualquer outra tela montada sobre ela
                // continuaria com o valor antigo por `staleTime`.
                void queryClient.invalidateQueries({
                  queryKey: getGetTeacherRateQueryKey(teacherId),
                });
              },
              onError: onReauthRequired,
            },
          ),
        );
      }}
    >
      <h3>{t('enrollment.teachers.rate.form')}</h3>

      {unexpected ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      <Field
        label={t('enrollment.teachers.rate.amountCents')}
        type="number"
        inputMode="numeric"
        min={1}
        required
        value={amountCents}
        hint={
          validAmount
            ? formatMoney({ amountCents: parsed, currency: 'BRL' }, locale)
            : t('enrollment.teachers.rate.amountHint')
        }
        error={
          amountCents.trim() !== '' && !validAmount
            ? t('enrollment.teachers.rate.amountInvalid')
            : undefined
        }
        onChange={(event) => setAmountCents(event.target.value)}
      />
      <Field
        label={t('enrollment.teachers.rate.validFrom')}
        hint={t('enrollment.teachers.rate.validFromHint')}
        type="date"
        required
        value={validFrom}
        onChange={(event) => setValidFrom(event.target.value)}
      />

      <div>
        <Button variant="primary" type="submit" loading={save.isPending} disabled={!canSave}>
          {t('enrollment.teachers.rate.save')}
        </Button>
      </div>

      {saved ? <Alert tone="success">{t('enrollment.teachers.rate.saved')}</Alert> : null}
    </form>
  );
}
